import { User, UserSubscription } from "@/connectors/remp/types/rempTypes";
import { PostType } from "@/core/types/contentTypes";
import { format } from "date-fns";
import { cs } from "date-fns/locale";
import { md5 } from "pure-md5";

type PaywallVersionType = "registration" | "free quota" | "hard" | "unlocked with subscription" | "free to read"; // in future: | "unlocked_by_friend"

export const buildPageViewOptions = (
  user: User | null | undefined,
  subscriptions: UserSubscription[] | null,
  post: PostType | null | undefined,
  pageType: string | null,
  hasContent: boolean,
  contentCategory?: string,
  hasGallery?: boolean,
  page_url?: string | null
) => {
  const inPwa = window.matchMedia("(display-mode: standalone)").matches;

  let contentType = "article";

  if (hasGallery || pageType === "article-gallery") {
    contentType += " | photogallery";
  }

  if (post?.audio?.url) {
    contentType += " | podcast";
  }

  const getContentName = (array: { name: string }[] | undefined) => {
    if (!!array?.length) {
      return array
        ?.map((i, k) => `${i.name} ${k + 1 !== array.length ? "|" : ""}`)
        ?.join(" ")
        ?.trim();
    }
    return "";
  };

  const getPaywallVersion = (): PaywallVersionType | "" => {
    const remainingUnlocks = 3 - (user?.user_meta?.unlocked_posts?.length || 0);
    if (!post?.paywallEnabled || document.referrer.includes("seznam.cz")) return "free to read";
    if (!user) return "registration";
    if (user && !!subscriptions?.length && post?.paywallEnabled) return "unlocked with subscription";
    if (user && subscriptions?.length === 0 && post?.paywallEnabled && remainingUnlocks > 0) return "free quota";
    if (user && subscriptions?.length === 0 && post?.paywallEnabled && remainingUnlocks === 0) return "hard";
    return "";
  };

  const getPageViewContent = () => {
    if (!hasContent || pageType === "homepage") {
      return "null";
    }
    return {
      content_type: contentType,
      // content_section: "example1|example2|example3", // TODO: implement value after customer requirement
      content_category: post?.categories ? getContentName(post.categories) : contentCategory || null,
      content_author: post?.authors ? getContentName(post.authors) : null,
      content_tags: post?.tags ? getContentName(post.tags) : null,
      content_topics: post?.topics ? getContentName(post.topics) : null,
      content_published:
        post?.yoastHeadJson?.article_published_time &&
        !isNaN(Date.parse(post?.yoastHeadJson?.article_published_time)) &&
        format(new Date(post?.yoastHeadJson?.article_published_time.split(" ")[0]), "dd. M. Y", { locale: cs }),
      content_has_image: !!post?.thumbnail.source ? "yes" : "no",
      content_has_gallery: post?.hasGallery || hasGallery ? "yes" : "no",
      content_has_audio: !!post?.audio?.url ? "yes" : "no",
      content_source: post?.contentSource || "",
      content_issue: post?.issue?.title || "",
      content_special: post?.postDetails?.pageHeaderSuperTitle || "",
      content_wordcount: post?.wordcount,
      content_paywall:
        !post?.paywallEnabled ||
        (!!user && !!subscriptions?.length && post?.paywallEnabled) ||
        document.referrer.includes("seznam.cz")
          ? "free"
          : "for subscribers",
      paywall_version: getPaywallVersion(),
    };
  };

  return {
    event: "pageView",
    user: {
      user_id: user?.id ?? "",
      is_user_logged: user ? "logged in" : "not logged in",
      user_hashed_email: user?.email ? md5(user?.email) : "",
      // paywall_version_id: "", TODO: implement missing value after customer requirement
      subscription_name: !!subscriptions?.length ? subscriptions[0].name : "",
      subscription_id: !!subscriptions?.length ? subscriptions[0].code : "",
      subscription_periodicity: !!subscriptions?.length ? subscriptions[0].access : [],
      subscription_end: !!subscriptions?.length
        ? format(new Date(subscriptions[0].end_at.split(" ")[0]), "dd. M. Y", { locale: cs })
        : 0,
      subscription_initial: !!subscriptions?.length
        ? format(new Date(subscriptions[0].start_at.split(" ")[0]), "dd. M. Y", { locale: cs })
        : 0,
      subscription_initial_timestamp: !!subscriptions?.length
        ? new Date(subscriptions[0].end_at.split(" ")[0]).getTime()
        : 0,
      subscription_end_timestamp: !!subscriptions?.length
        ? new Date(subscriptions[0].start_at.split(" ")[0]).getTime()
        : 0,
    },
    page: {
      page_language: process.env.NEXT_PUBLIC_DEFAULT_API_LANGUAGE_CODE!,
      page_type: pageType,
      environment_type: inPwa ? "pwa" : "web",
      // page_url: page_url,
      // ab_version: "", TODO: implement missing value after customer requirement
    },
    content: getPageViewContent(),
  };
};
